import { useAtomValue } from "jotai";
import { useRouter } from "next/router";
import pym, { type Parent } from "pym.js";
import { useEffect, useMemo, useRef, useState } from "react";

import { useLoginDialogStateHelper } from "scmp-app/components/login-dialog/hooks";
import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import type { Event } from "scmp-app/components/tracking/google-analytics-4/types";
import { rosettaAtom } from "scmp-app/lib/rosetta/atoms";
import { safeSetLocalStorage } from "scmp-app/scmp-sdk";

import GameOgImage from "./game-og.png";
import { createGameUrl } from "./helpers";
import WordSearchOgImage from "./word-search-og.png";

export const gameLocalStorageSyncKey = [
  "word-search-how-to-play-shown",
  "scmp-crosswords-typeform-is-shown",
];
/**
 * Collect specific localStorage data by keys
 */
const collectGameLocalStorage = (keys: string[]): Record<string, string> => {
  if (typeof window === "undefined" || typeof localStorage === "undefined") {
    return {};
  }

  const localStorageData: Record<string, string> = {};

  try {
    keys.forEach(key => {
      const value = localStorage.getItem(key);
      if (value !== null) {
        localStorageData[key] = value;
      }
    });
  } catch (error) {
    console.error("Error collecting localStorage data:", error);
  }

  return localStorageData;
};

// Define the message event data structure
export type GameEvent = {
  destination?: string;
  event?: Event;
  gameType?: "crossword_cryptic" | "crossword_quick" | "word_search";
  localStorageKeyName?: string;
  localStorageKeyValue?: string;
  pathname?: string;
  type: string;
};

export const useGameRouteChange = () => {
  const router = useRouter();
  const routerReference = useRef(router);

  const { openLoginDialog } = useLoginDialogStateHelper();

  useEffect(() => {
    routerReference.current = router;
  }, [router]);

  useEffect(() => {
    const handleMessage = function (event: MessageEvent) {
      const data = event.data as GameEvent;
      if (data.type === "SCMP_GAME_EVENT_TRACKING" && data?.event) {
        sendGA4Tracking(data.event);
      }

      switch (data?.type) {
        case "SCMP_GAME_LOCAL_STORAGE_UPDATE":
          if (data?.localStorageKeyName && data?.localStorageKeyValue) {
            safeSetLocalStorage(data.localStorageKeyName, data.localStorageKeyValue);
          }
          break;
        case "SCMP_GAME_LOGIN_REQUEST":
        case "SCMP_GAME_SIGNUP_REQUEST":
          openLoginDialog({
            destination: data?.destination
              ? `${window.location.origin}${data.destination}`
              : window.location.href,
            ...(data?.gameType ? { ga4CustomParameter: { trigger_point: data.gameType } } : {}),
          });
          break;
        case "SCMP_GAME_ROUTE_CHANGE":
          const { pathname } = data;
          const decodedPathname = decodeURIComponent(pathname ?? "");

          if (
            data.type === "SCMP_GAME_ROUTE_CHANGE" &&
            window.location.pathname !== decodedPathname
          ) {
            const [pathname, query] = decodedPathname.split("?");

            void routerReference.current.push(
              {
                pathname: pathname ?? "",
                query: query ?? "",
              },
              undefined,
              {
                scroll: false,
                shallow: true,
              },
            );
          }
          break;
      }
    };
    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [openLoginDialog]);

  return null;
};

interface UseGameIframeOptions {
  date?: string | string[] | undefined;
  path?: string;
  queryString?: string;
}

/**
 * Hook to handle game iframe creation and pym.js initialization
 */
export const useGameIframe = ({
  date,
  path = "games",
  queryString = "",
}: UseGameIframeOptions = {}) => {
  const [isLoaded, setIsLoaded] = useState<boolean>(false);

  const pymJsReference = useRef<null | Parent>(null);

  const asyncRosettaState = useAtomValue(rosettaAtom);
  const isSubscriber = useMemo(
    () => asyncRosettaState?.result?.user?.isSCMPSubscriber ?? false,
    [asyncRosettaState?.result?.user?.isSCMPSubscriber],
  );

  useGameRouteChange();

  const getParameters = () => {
    const parameters = new URLSearchParams(queryString);
    const items: Record<string, string> = {};
    for (const [key, value] of parameters.entries()) {
      items[key] = value;
    }

    return items;
  };
  // Generate URL and ID
  const iframeUrl = createGameUrl(
    date && typeof date === "string" ? `${path}/${date}` : path,
    getParameters(),
  );
  const iframeId = "scmp-game-iframe";

  // Initialize pym.js
  useEffect(() => {
    if (!iframeId || !iframeUrl || isLoaded) return;

    // Create the Parent instance
    pymJsReference.current = new pym.Parent(iframeId, iframeUrl, {});

    setIsLoaded(true);
  }, [iframeId, iframeUrl, isLoaded]);

  // Height is automatically set by pym.js, this onMessage is to trigger the handShake for sending subscriber status back to the game
  const [handShake, setHandShake] = useState(false);
  pymJsReference.current?.onMessage("height", () => setHandShake(true));
  useEffect(() => {
    if (!handShake || asyncRosettaState?.status === "loading" || asyncRosettaState === undefined)
      return;
    pymJsReference.current?.sendMessage(
      "SCMP_PWA_SUBSCRIBER_STATUS",
      JSON.stringify({
        isSubscriber: isSubscriber ? "1" : "0",
        subscriberPhase: asyncRosettaState?.result?.tracking?.ga4.subscriberPhase ?? "",
      }),
    );
    const localStorageData = collectGameLocalStorage(gameLocalStorageSyncKey);
    pymJsReference.current?.sendMessage(
      "SCMP_PWA_INIT_LOCAL_STORAGE",
      JSON.stringify(localStorageData),
    );
  }, [
    handShake,
    isSubscriber,
    asyncRosettaState,
    asyncRosettaState?.status,
    asyncRosettaState?.result?.tracking?.ga4.subscriberPhase,
  ]);

  return {
    iframeId,
  };
};

export const useGameSeo = () => ({
  additionalMetaTags: [
    {
      content:
        "South China Morning Post, crossword, puzzles, quiz, lifestyle, games, online games, mobile games, free games, app games, quick crossword, cryptic crossword, daily crossword, word search",
      name: "keywords",
    },
    {
      content:
        "South China Morning Post, crossword, puzzles, quiz, lifestyle, games, online games, mobile games, free games, app games, quick crossword, cryptic crossword, daily crossword, word search",
      name: "news_keywords",
    },
  ],
  description:
    "Play the South China Morning Post's crossword and other puzzles with SCMP Games. Get full access to SCMP Games on our app, or subscribers can play across any platform.",
  openGraph: {
    description: "Play the South China Morning Post's crossword and other puzzles with SCMP Games.",
    images: [
      {
        url: GameOgImage.src,
      },
    ],
    title:
      "Download the South China Morning Post app for full access to SCMP Games, including crosswords and puzzles.",
  },
  title: "Crosswords, puzzles and quizzes",
});

export const useQuickCrosswordSeo = (gameTitle = "PostMag Quick Crossword") => ({
  additionalMetaTags: [
    {
      content:
        "South China Morning Post, crossword, puzzles, PostMag quick crossword, word games, lifestyle, games, online games, mobile games, free games, app games, quick crossword, daily crossword, daily games, easy crossword",
      name: "keywords",
    },
    {
      content:
        "South China Morning Post, crossword, puzzles, PostMag quick crossword, word games, lifestyle, games, online games, mobile games, free games, app games, quick crossword, daily crossword, daily games, easy crossword",
      name: "news_keywords",
    },
  ],
  description: `Play the ${gameTitle} on SCMP Games. Get full access to SCMP Games on our app, or subscribers can play across any platform.`,
  openGraph: {
    description: `Play the ${gameTitle} and other puzzles with SCMP Games.`,
    images: [
      {
        url: GameOgImage.src,
      },
    ],
    title: `Download the South China Morning Post app for full access to SCMP Games, including the ${gameTitle}.`,
  },
  title: gameTitle,
});

export const useCrypticCrosswordSeo = () => ({
  additionalMetaTags: [
    {
      content:
        "South China Morning Post, crossword, puzzles, SCMP cryptic crossword, word games, lifestyle, games, online games, mobile games, free games, app games, cryptic crossword, daily crossword, daily games, hard crossword",
      name: "keywords",
    },
    {
      content:
        "South China Morning Post, crossword, puzzles, SCMP cryptic crossword, word games, lifestyle, games, online games, mobile games, free games, app games, cryptic crossword, daily crossword, daily games, hard crossword",
      name: "news_keywords",
    },
  ],
  description:
    "Play the SCMP Cryptic Crossword on SCMP Games. Get full access to SCMP Games on our app, or subscribers can play across any platform.",
  openGraph: {
    description: "Play the SCMP Cryptic Crossword and other puzzles with SCMP Games.",
    images: [
      {
        url: GameOgImage.src,
      },
    ],
    title:
      "Download the South China Morning Post app for full access to SCMP Games, including the SCMP Cryptic Crossword.",
  },
  title: "SCMP Cryptic Crossword",
});

export const useWordSearchSeo = () => ({
  additionalMetaTags: [
    {
      content:
        "South China Morning Post, word search, puzzles, word games, lifestyle, games, online games, mobile games, free games, app games, daily games, easy games",
      name: "keywords",
    },
    {
      content:
        "South China Morning Post, word search, puzzles, word games, lifestyle, games, online games, mobile games, free games, app games, daily games, easy games",
      name: "news_keywords",
    },
  ],
  description:
    "Play the South China Morning Post's Word Search puzzle on SCMP Games. Get full access to SCMP Games on our app, or subscribers can play across any platform.",
  openGraph: {
    description:
      "Play the South China Morning Post's Word Search and other puzzles with SCMP Games.",
    images: [
      {
        url: WordSearchOgImage.src,
      },
    ],
    title:
      "Download the South China Morning Post app for full access to SCMP Games, including Word Search.",
  },
  title: "Word Search",
});
